import {
  Children,
  createContext,
  forwardRef,
  isValidElement,
  useContext,
} from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { cn } from "../lib/cn";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "../lib/icons";
import { useFieldControlProps } from "../molecules/Field";

/**
 * Select — manifest §Select.
 *
 *   <Select value={v} onValueChange={setV} placeholder="Pick…">
 *     <SelectTrigger />
 *     <SelectLabel>Fruits</SelectLabel>
 *     <SelectItem value="apple">Apple</SelectItem>
 *   </Select>
 *
 * `Select` composes the Root plus the internal portalled Content/Viewport/
 * scroll buttons. The `SelectTrigger` child renders inline; every other
 * child (items, labels) is routed into the popper viewport. The Root-level
 * `placeholder` reaches the trigger through context.
 */

const SelectPlaceholderContext = createContext<string | undefined>(undefined);

/** Split Select children into the inline trigger and the popper contents. */
function partitionSelectChildren(
  children: React.ReactNode,
): [trigger: React.ReactNode, options: React.ReactNode[]] {
  const childArray = Children.toArray(children);
  const trigger = childArray.find(
    (child) => isValidElement(child) && child.type === SelectTrigger,
  );
  const options = childArray.filter((child) => child !== trigger);
  return [trigger, options];
}

export interface SelectProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  /** Shown in the trigger when no value is selected (content-secondary). */
  placeholder?: string;
  children: React.ReactNode;
}

export function Select({
  value,
  defaultValue,
  onValueChange,
  disabled,
  placeholder,
  children,
}: SelectProps) {
  const [trigger, options] = partitionSelectChildren(children);
  return (
    <SelectPlaceholderContext.Provider value={placeholder}>
      <SelectPrimitive.Root
        value={value}
        defaultValue={defaultValue}
        onValueChange={onValueChange}
        disabled={disabled}
      >
        {trigger}
        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            position="popper"
            sideOffset={4}
            collisionPadding={8}
            className="z-(--z-popover) w-(--radix-select-trigger-width) min-w-(--radix-select-trigger-width) overflow-hidden rounded-lg border border-border-default bg-surface-default shadow-lg"
          >
            <SelectPrimitive.ScrollUpButton className="hidden h-6 cursor-default items-center justify-center text-content-secondary [@media(pointer:fine)]:flex">
              <ChevronUpIcon className="size-4" />
            </SelectPrimitive.ScrollUpButton>
            <SelectPrimitive.Viewport className="max-h-72 min-h-0 p-1">
              {options}
            </SelectPrimitive.Viewport>
            <SelectPrimitive.ScrollDownButton className="hidden h-6 cursor-default items-center justify-center text-content-secondary [@media(pointer:fine)]:flex">
              <ChevronDownIcon className="size-4" />
            </SelectPrimitive.ScrollDownButton>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    </SelectPlaceholderContext.Provider>
  );
}
Select.displayName = "Select";

export interface SelectTriggerProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> {}

/** Input-like button that reflects the selected item text or the placeholder. */
export const SelectTrigger = forwardRef<HTMLButtonElement, SelectTriggerProps>(
  (
    {
      className,
      id,
      "aria-describedby": ariaDescribedBy,
      "aria-invalid": ariaInvalid,
      "aria-required": ariaRequired,
      ...props
    },
    ref,
  ) => {
    const placeholder = useContext(SelectPlaceholderContext);
    const fieldControlProps = useFieldControlProps({
      id,
      describedBy: ariaDescribedBy,
      invalid: ariaInvalid,
      required: ariaRequired,
    });
    return (
      <SelectPrimitive.Trigger
        ref={ref}
        data-slot="select-trigger"
        {...fieldControlProps}
        className={cn(
          "flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-border-control bg-surface-default px-3 type-body-md text-content-primary select-none",
          "outline-none transition-colors duration-150",
          "focus-visible:border-border-focused focus-visible:ring-2 focus-visible:ring-border-focused focus-visible:ring-offset-1 focus-visible:ring-offset-surface-canvas",
          "data-[state=open]:border-border-focused",
          "disabled:cursor-not-allowed disabled:opacity-38",
          className,
        )}
        {...props}
      >
        <SelectPrimitive.Value
          className="min-w-0 flex-1 text-left"
          placeholder={
            <span className="text-content-secondary">{placeholder}</span>
          }
        />
        <SelectPrimitive.Icon>
          <ChevronDownIcon className="size-4 shrink-0 text-content-secondary" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
    );
  },
);
SelectTrigger.displayName = "SelectTrigger";

export interface SelectItemProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> {
  description?: React.ReactNode;
}

export const SelectItem = forwardRef<HTMLDivElement, SelectItemProps>(
  ({ className, children, description, ...props }, ref) => {
    return (
      <SelectPrimitive.Item
        ref={ref}
        data-slot="select-item"
        className={cn(
          "relative flex w-full cursor-default select-none items-start rounded-md py-2 pl-8 pr-2 type-body-md text-content-primary outline-none",
          "data-highlighted:bg-surface-raised",
          "data-disabled:pointer-events-none data-disabled:text-content-disabled",
          className,
        )}
        {...props}
      >
        <span className="absolute left-2 top-2 flex size-4 items-center justify-center">
          <SelectPrimitive.ItemIndicator>
            <CheckIcon className="size-4" />
          </SelectPrimitive.ItemIndicator>
        </span>
        <span className="min-w-0">
          <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
          {description ? (
            <span className="mt-0.5 block type-caption text-content-secondary">
              {description}
            </span>
          ) : null}
        </span>
      </SelectPrimitive.Item>
    );
  },
);
SelectItem.displayName = "SelectItem";

export interface SelectGroupProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Group> {}

/** Groups items under a SelectLabel — SelectLabel must sit inside one. */
export const SelectGroup = forwardRef<HTMLDivElement, SelectGroupProps>(
  ({ ...props }, ref) => {
    return <SelectPrimitive.Group ref={ref} {...props} />;
  },
);
SelectGroup.displayName = "SelectGroup";

export interface SelectLabelProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label> {}

export const SelectLabel = forwardRef<HTMLDivElement, SelectLabelProps>(
  ({ className, ...props }, ref) => {
    return (
      <SelectPrimitive.Label
        ref={ref}
        className={cn(
          "type-overline text-content-secondary px-2 py-1.5",
          className,
        )}
        {...props}
      />
    );
  },
);
SelectLabel.displayName = "SelectLabel";
